import { Inject, Injectable } from '@angular/core';
import { Policy, Role } from 'src/app/global/policy-types';
import {
  AuthenticationEvent,
  AuthenticationService,
} from './authentication.service';
import { HttpClientService } from './http-client.service';
import { BehaviorSubject } from 'rxjs';
import { WINDOW } from './window.service';
import { BrandingService } from './branding.service';
import { HOST_CONFIG, HostConfig } from 'src/app/config/AppInitializer.factory';

export enum PolicyEvent {
  PolicyLoaded
}

export class PolicyEventEmitter {
  private events: Record<PolicyEvent, Function[]> = {
    [PolicyEvent.PolicyLoaded]: []
  };

  subscribe(event: PolicyEvent, callback: Function): void {
    this.events[event].push(callback);
  }

  unsubscribe(event: PolicyEvent, callback: Function): void {
    const index = this.events[event].indexOf(callback);
    if (index != -1) {
      this.events[event].splice(index, 1);
    }
  }

  trigger(event: PolicyEvent, data?: any): void {
    this.events[event].forEach(callback => callback(data));
  }
}

@Injectable({
  providedIn: 'root',
})
export class PolicyService {
  constructor(
    private authenticationService: AuthenticationService,
    private httpClientService: HttpClientService,
    @Inject(WINDOW) private window: Window,
    @Inject(HOST_CONFIG) private hostConfig: HostConfig,
    private brandingService: BrandingService
  ) {
    this.authenticationService.events.subscribe(
      AuthenticationEvent.Authenticated,
      async () => await this.getPolicy()
    );
    this.authenticationService.events.subscribe(
      AuthenticationEvent.ImpersonationStarted,
      async () => await this.getPolicy()
    );
    this.authenticationService.events.subscribe(
      AuthenticationEvent.ImpersonationStopped,
      async () => await this.getPolicy()
    );
  }

  public readonly events = new PolicyEventEmitter();

  private policySubject: BehaviorSubject<Policy | null> = new BehaviorSubject<Policy | null>(null);
  private isAuthorized = new BehaviorSubject<boolean>(true);
  isAuthorized$ = this.isAuthorized.asObservable();

  private isPolicyLoading: boolean = false; // Guard against simultaneous requests

  private async getPolicy(): Promise<void> {
    const accessToken = await this.authenticationService.getAccessToken();
    if (accessToken === undefined || this.isPolicyLoading) {
      // Premature call or already loading. Skip fetching policy.
      return;
    }

    this.isPolicyLoading = true;
    try {
      const { data } = await this.httpClientService.get<Policy>('/policy');
      const isAuthorized = data && data.roles.length > 0;
      this.isAuthorized.next(isAuthorized);
      if (data.roles.findIndex(role => role === Role.CorporateAccountManager) !== -1) {
        if (this.brandingService.isProgramHub) {
          console.log(`Redirecting to ${this.hostConfig.ebrUrl}`);
          this.window.location.href = this.hostConfig.ebrUrl;
        }
      }

      this.policySubject.next(data);
      this.events.trigger(PolicyEvent.PolicyLoaded);
    } finally {
      this.isPolicyLoading = false;
    }
  }

  public get currentPolicy(): Policy | null {
    const policy = this.policySubject.getValue();
    if (policy == null) {
      this.getPolicy();
    }

    return policy;
  }
}
